<template>
  <div class="ca-header__actions gap-size-4">
    <!-- Hamburger - apenas no menu normal, mobile -->
    <button
      v-if="!searchOpen && !isSimplifiedMenu"
      class="ca-header__hamburgerbtn rounded-xs"
      :class="{ 'is-open': mobileMenuOpen, 'is-collapsing': isCollapsing }"
      @click="emit('toggle-mobile-menu')"
      aria-label="Menu"
    >
      <span class="ca-header__hicon">
        <span class="ca-header__hline ca-header__hline--top"></span>
        <span class="ca-header__hline ca-header__hline--mid"></span>
        <span class="ca-header__hline ca-header__hline--bot"></span>
      </span>
    </button>
    <!-- No modo simplificado usa homebankingbtn mas precisa de estar sempre visível,
         incluindo em mobile onde o CSS normal esconderia esse botão. -->
    <button
      :class="[
        isSimplifiedMenu
          ? 'ca-header__homebankingbtn gap-size-3 pt-size-3 pr-size-5 pb-size-3 pl-size-4 rounded-xs'
          : 'ca-header__abrircontabtn rounded-xs px-size-5 py-size-3',
        { 'ca-header__homebankingbtn--simplified': isSimplifiedMenu },
      ]"
      class="caption-medium"
      v-if="abrirContaTitle.length > 0 && abrirContaUrl.length > 0"
      @click="emit('abrir-conta')"
    >
      <component v-if="isSimplifiedMenu" :is="GlossarioIcon" />
      <span>{{ abrirContaTitle }}</span>
    </button>

    <!-- Botão homebanking - apenas no menu normal -->
    <CaTooltip
      :text="homebankingTitle"
      position="bottom"
      :disabled="!isTextHidden"
    >
      <button
        v-if="!isSimplifiedMenu && homebankingUrl.length > 0"
        class="ca-header__homebankingbtn gap-size-3 pt-size-3 pr-size-5 pb-size-3 pl-size-4 rounded-xs caption-medium"
        @click="emit('homebanking')"
      >
        <component :is="LockIcon" />
        <span class="is-special-width">{{ homebankingTitle }}</span>
      </button>
    </CaTooltip>
    <!-- Search bar expandível - apenas no menu normal e fora da página de resultados -->
    <div
      v-if="!isSimplifiedMenu && !isSearchResultsPage"
      class="ca-header__search-bar p-size-3 js-search-area"
      :class="{ 'is-open': searchBarOpen && !isMobileOrTablet }"
      ref="searchBarRef"
    >
      <input
        v-show="searchOpen && !isMobileOrTablet"
        id="globalSearch"
        type="text"
        ref="searchInputRef"
        class="ca-header__search-bar-input caption-medium js-search-area"
        placeholder="Procurar no site"
        :value="searchQuery"
        @input="emit('update:searchQuery', $event.target.value)"
        @focus="emit('open-search-results')"
        @keydown.enter="emit('submit-search')"
      />
      <button
        v-show="searchOpen && searchQuery && !isMobileOrTablet"
        class="ca-header__search-bar-close js-search-area"
        @click="emit('clear-search')"
        aria-label="Limpar pesquisa"
      >
        <component :is="SearchCloseIcon" />
      </button>

      <button
        :class="{ active: searchBarOpen && !isMobileOrTablet }"
        class="ca-header__searchbtn js-search-area"
        :disabled="isLoading"
        aria-label="Barra de Pesquisa"
        v-if="!mobileMenuOpen"
        @click="
          isMobileOrTablet
            ? emit('open-mobile-search')
            : searchBarOpen
              ? emit('close-search')
              : emit('open-search')
        "
      >
        <component v-if="!isLoading" :is="SearchIcon" />
        <div v-else class="ca-header__search-shimmer"></div>
      </button>
    </div>
  </div>
</template>

<script setup>
import SearchIcon from "@assets/search-icon.svg";
import SearchCloseIcon from "@assets/search-close.svg";
import GlossarioIcon from "@assets/glossario.svg";
import LockIcon from "@assets/lock.svg";
import CaTooltip from "../../base/CaTooltip.vue";
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";

const props = defineProps({
  isSimplifiedMenu: { type: Boolean, default: false },
  isSearchResultsPage: { type: Boolean, default: false },
  searchOpen: { type: Boolean, default: false },
  searchBarOpen: { type: Boolean, default: false },
  searchQuery: { type: String, default: "" },
  mobileMenuOpen: { type: Boolean, default: false },
  isCollapsing: { type: Boolean, default: false },
  isMobileOrTablet: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  abrirContaTitle: { type: String, default: "" },
  abrirContaUrl: { type: String, default: "" },
  homebankingTitle: { type: String, default: "" },
  homebankingUrl: { type: String, default: "" },
});

const emit = defineEmits([
  "toggle-mobile-menu",
  "abrir-conta",
  "homebanking",
  "open-search",
  "close-search",
  "clear-search",
  "open-mobile-search",
  "open-search-results",
  "submit-search",
  "update:searchQuery",
]);

const searchInputRef = ref(null);
const searchBarRef = ref(null);

watch(
  () => props.searchOpen,
  (isOpen) => {
    if (isOpen && !props.isMobileOrTablet) {
      nextTick(() => {
        setTimeout(() => {
          searchInputRef.value?.focus();
        }, 300);
      });
    }
  },
);

const isTextHidden = ref(false);

const checkWidth = () => {
  isTextHidden.value = window.innerWidth >= 1024 && window.innerWidth <= 1288;
};

onMounted(() => {
  checkWidth();
  window.addEventListener("resize", checkWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkWidth);
});

defineExpose({ searchBarRef, searchInputRef });
</script>

<style scoped lang="scss">
.ca-header__actions {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  margin-left: auto;
  flex: 1 1 auto;
  min-width: 0;
}

.ca-header__search-bar {
  display: flex;
  align-items: center;
  gap: var(--size-3);
  border: 1px solid transparent;
  border-radius: var(--size-6);
  max-height: 48px;
  background: white;
  width: 48px;
  max-width: 48px;
  min-width: 48px;
  flex: 0 0 48px;
  overflow: visible;
  transition:
    width 0.9s cubic-bezier(0.4, 0, 0.2, 1),
    max-width 0.9s cubic-bezier(0.4, 0, 0.2, 1),
    flex 0.9s cubic-bezier(0.4, 0, 0.2, 1),
    margin-left 0.9s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.9s cubic-bezier(0.4, 0, 0.2, 1),
    border-radius 0.9s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-open {
    flex: 1 1 auto;
    max-width: 100%;
    margin-left: 0;
    border-color: var(--ca-color-theme-primary);
    border-radius: var(--size-3);
  }

  &-input {
    order: 1;
    flex: 1 1 auto;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    caret-color: var(--ca-color-theme-primary);
    color: var(--ca-color-text-body);
    letter-spacing: var(--letter-spacing-sm);
    padding: var(--size-0) var(--size-2);

    &::placeholder {
      color: var(--ca-color-text-body-variant);
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px white inset;
      box-shadow: 0 0 0 1000px white inset;
    }
  }

  &-close {
    order: 2;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 0;
    margin-left: auto;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.25s ease;

    .ca-header__search-bar.is-open & {
      opacity: 1;
      transition: opacity 0.3s ease 0.3s;
    }

    &:focus-visible {
      outline: 1px solid var(--ca-color-feedback-info);
    }
  }
}

.ca-header__searchbtn {
  flex-shrink: 0;
  order: -1;
  background: white;
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 100px;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s ease;

  &:hover {
    background: var(--ca-color-hover-pressed-neutral-hover);
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
  }
  &:active {
    background: var(--ca-color-hover-pressed-neutral-pressed);
  }
  &.disabled {
    background: none;
    opacity: 0.5;
  }

  .ca-header__search-bar.is-open & {
    width: auto;
    min-width: 0;
    height: auto;
    padding: 0;
    border-radius: 0;
    background: transparent;
    cursor: default;
    pointer-events: none;
  }

  .ca-header__search-bar.is-open &:hover {
    background: transparent;
  }
}

.ca-header__homebankingbtn {
  background: white;
  min-height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--ca-color-theme-primary);
  color: var(--ca-color-theme-primary);
  letter-spacing: var(--letter-spacing-xs);
  cursor: pointer;

  &:hover {
    background: var(--ca-color-hover-pressed-container-high-hover);
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
  }
  &:active {
    background: var(--ca-color-hover-pressed-inverted-hover-light);
  }
  &.disabled {
    opacity: 0.5;
  }
}

.ca-header__abrircontabtn {
  background: var(--ca-color-theme-primary);
  min-height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  color: var(--ca-color-text-inverted);
  letter-spacing: var(--letter-spacing-xs);
  cursor: pointer;

  &:hover {
    background: var(--ca-color-hover-pressed-primary-hover);
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
  }
  &:active {
    background: var(--ca-color-hover-pressed-primary-pressed);
  }
  &.disabled {
    opacity: 0.5;
  }
}

.ca-header__hamburgerbtn {
  display: none;
  background: var(--ca-color-text-inverted);
  width: 48px;
  height: 48px;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;

  &:hover {
    background: var(--ca-color-hover-pressed-container-high-hover);
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
  }
}

.ca-header__hicon {
  position: relative;
  width: 20px;
  height: 14px;
  display: block;
}

.ca-header__hline {
  position: absolute;
  left: 0;
  width: 20px;
  height: 2px;
  background: var(--ca-color-text-primary);
  border-radius: 2px;
  transition:
    top 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &--top {
    top: 0;
  }
  &--mid {
    top: 6px;
  }
  &--bot {
    top: 12px;
  }
}

.ca-header__hamburgerbtn.is-collapsing:not(.is-open) {
  .ca-header__hline--top {
    top: 6px;
  }
  .ca-header__hline--mid {
    top: 6px;
  }
  .ca-header__hline--bot {
    top: 6px;
  }
}

.ca-header__hamburgerbtn.is-open {
  .ca-header__hline--top {
    top: 6px;
    transform: rotate(45deg);
  }
  .ca-header__hline--mid {
    opacity: 0;
  }
  .ca-header__hline--bot {
    top: 6px;
    transform: rotate(-45deg);
  }
}

.ca-header__hamburgerbtn.is-collapsing.is-open {
  .ca-header__hline--top {
    top: 6px;
    transform: rotate(0deg);
  }
  .ca-header__hline--mid {
    opacity: 0;
    top: 6px;
  }
  .ca-header__hline--bot {
    top: 6px;
    transform: rotate(0deg);
  }
}

@media (max-width: 367px) {
  .ca-header__actions {
    gap: 0;
  }
}

@media (max-width: 1164px) {
  .ca-header__actions {
    gap: 8px;
  }

  .ca-header__search-bar {
    width: 48px;
    max-width: 48px;
    min-width: 48px;
    flex: 0 0 48px;

    &.is-open {
      flex: 0 0 48px;
      max-width: 48px;
      margin-left: 0;
      border-color: transparent;
      border-radius: var(--size-5);
    }
  }

  .ca-header__homebankingbtn {
    display: none;
  }

  .ca-header__homebankingbtn--simplified {
    display: flex !important;
  }

  .ca-header__abrircontabtn {
    display: none;
  }

  .ca-header__hamburgerbtn {
    display: flex;

    &:hover {
      background: none;
    }
  }
}

@media (max-width: 367px) {
  .ca-header__actions {
    gap: 0;
  }
}

.is-special-width {
  display: block;
}

@media (min-width: 1024px) and (max-width: 1289px) {
  .is-special-width {
    display: none;
  }

  .ca-header__homebankingbtn {
    padding: var(--size-3) var(--size-4) var(--size-3) var(--size-4);
  }
}
</style>
